import { useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";

import actions from "../actions";
import * as util from "../lib/common";
import { RootState } from "../reducers/types/root-state";
import * as constants from "@/constants";

const X_SXP_USER_ID = "X-SXP-User-Id";
const X_SXP_CONSTRUCTION_ID = "X-SXP-Construction-Id";
const X_SXP_LANGUAGE = "X-SXP-Language";
const X_SXP_OPTIONS = "X-SXP-Options";

const blobTypes = ["excel", "pdf", "image", "application/zip"];

const isBlob = (contentType: string) => {
  return blobTypes.some((t) => contentType.indexOf(t) !== -1);
};

const convert = async (response: Response, contentType: string) => {
  if (isBlob(contentType)) {
    return response.blob();
  }

  if (contentType.indexOf("text/csv") !== -1) {
    return response.text();
  }

  return response.json();
};

const getFilenameFromContentDisposition = (contentDisposition: string) => {
  const regex = /filename[^;=\n]*=(UTF-8(['"]*))?(.*)/;
  const matches = regex.exec(contentDisposition);
  let filename = "";

  if (matches !== null && matches[3]) {
    filename = matches[3].replace(/['"]/g, "");
  }

  return decodeURIComponent(filename);
};

export interface ApiOptions extends RequestInit {
  /**
   * APIエラー時の処理を呼び出し元で独自に行いたい場合に指定する。
   *
   * 指定した場合、共通エラー表示用の APP_SHOW_ERROR は dispatch されない。
   * そのため、フォームのバリデーション表示や画面固有のエラー処理など、
   * 共通エラーダイアログを使わないケースで利用する。
   */
  errorCallback?: (error: any) => void;
  /**
   * 共通エラー表示後、ユーザー操作により再読み込みが必要になった場合の処理を指定する。
   *
   * errorCallback が未指定の場合のみ、APP_SHOW_ERROR の reload として error.ts に渡される。
   * error.ts 側では画面種別ごとの標準リロード処理が優先され、
   * それに該当しない場合にこの callback が実行される。
   *
   * 排他エラーなど、エラー表示後に最新データを再取得したいケースで利用する。
   */
  reloadCallback?: (error: any) => void;
}

/**
 * excel/pdf/画像等のバイナリレスポンスを取得した場合の戻り値の型。
 * `get`/`post` 等でこの形になるのは、レスポンスのcontent-typeが
 * blob対象（`isBlob`参照）かつ Content-Disposition: attachment の場合のみ。
 */
export type ApiFileResult = {
  data: Blob;
  fileName: string;
};

export const useApi = () => {
  const dispatch = useDispatch();
  const constructionId = useSelector((state: RootState) => state.construction.constructionId);
  const lang = useSelector((state: RootState) => state.app.lang);
  const login = useSelector((state: RootState) => (state as any).login);

  const callApi = useCallback(
    async (endpoint: string, options: ApiOptions = {}): Promise<any> => {
      const { errorCallback, reloadCallback, ...fetchOptions } = options;
      const headers: Record<string, string> = (fetchOptions.headers as Record<string, string>) || {};

      const sessionToken = util.getSessionToken();
      const userId = util.getUserId();
      // ユーザ紐づき工事一覧(/constructions)に選択中の工事を渡して紐付かない場合に認証エラーになるので無視する
      const currentConstructionId = endpoint.endsWith("/constructions") ? 0 : constructionId;

      // Authorization & User-Id
      if (sessionToken === null) {
        const { authResult } = login || {};
        if (authResult && authResult.session_token !== "") {
          headers["Authorization"] = `Bearer ${authResult.session_token}`;
        }
      } else {
        headers["Authorization"] = `Bearer ${sessionToken}`;
      }

      if (userId === null) {
        const { authResult } = login || {};
        if (authResult) {
          headers[X_SXP_USER_ID] = authResult.user_id;
        }
      } else {
        headers[X_SXP_USER_ID] = String(userId);
      }

      // Construction-Id
      if (currentConstructionId !== 0) {
        headers[X_SXP_CONSTRUCTION_ID] = String(currentConstructionId);
      }

      // Other headers
      headers[X_SXP_LANGUAGE] = lang;
      headers[X_SXP_OPTIONS] = JSON.stringify({ device: "pc" });

      const params: RequestInit = {
        mode: "cors",
        cache: "no-cache",
        method: fetchOptions.method || "GET",
        ...fetchOptions,
        headers,
      };

      try {
        const response = await fetch(endpoint, params);

        if (response.status === 401 || response.status === 503) {
          dispatch(actions.login.shouldLogout());
        }

        if (response.ok) {
          const contentType = response.headers.get("content-type") || "";
          const contentDisposition = response.headers.get("content-disposition");
          let fileName = "";

          if (contentDisposition && contentDisposition.indexOf("attachment") !== -1) {
            fileName = getFilenameFromContentDisposition(contentDisposition);
          }

          const data = await convert(response, contentType);

          if (isBlob(contentType) && fileName !== "") {
            return { data, fileName };
          }

          return data;
        }

        throw response;
      } catch (error: any) {
        let errorData = error;
        if (error instanceof Response) {
          const statusCode = error.status;
          try {
            errorData = { ...(await error.json()), statusCode };
          } catch (e) {
            errorData = error;
          }
        }

        if (errorCallback) {
          errorCallback(errorData);
        } else {
          dispatch({
            type: constants.APP_SHOW_ERROR,
            payload: errorData,
            error: true,
            reload: options.reloadCallback,
          });
        }
        return;
      }
    },
    [dispatch, lang, constructionId, login]
  );

  return {
    // callApi はエラー時に undefined を返すため、戻り値の型には必ず `| undefined` を含める。
    get: <T = unknown>(endpoint: string, options?: Omit<ApiOptions, "method">): Promise<T | undefined> =>
      callApi(endpoint, { ...options, method: "GET" }),
    post: <T = unknown>(
      endpoint: string,
      body?: any,
      options?: Omit<ApiOptions, "method" | "body">
    ): Promise<T | undefined> =>
      callApi(endpoint, {
        ...options,
        method: "POST",
        body: body instanceof FormData ? body : JSON.stringify(body),
        headers: body instanceof FormData || !body ? {} : { "Content-Type": "application/json" },
      }),
    put: <T = unknown>(
      endpoint: string,
      body?: any,
      options?: Omit<ApiOptions, "method" | "body">
    ): Promise<T | undefined> =>
      callApi(endpoint, {
        ...options,
        method: "PUT",
        body: JSON.stringify(body),
        headers: { "Content-Type": "application/json" },
      }),
    patch: <T = unknown>(
      endpoint: string,
      body?: any,
      options?: Omit<ApiOptions, "method" | "body">
    ): Promise<T | undefined> =>
      callApi(endpoint, {
        ...options,
        method: "PATCH",
        body: JSON.stringify(body),
        headers: { "Content-Type": "application/json" },
      }),
    del: <T = unknown>(endpoint: string, options?: Omit<ApiOptions, "method">): Promise<T | undefined> =>
      callApi(endpoint, { ...options, method: "DELETE" }),
  };
};
